/**
 * FE-T04 / A-T04 — Invite form inside the SubDelegatesModal.
 *
 * Collapsible section: a single "Invite sub-delegate" trigger expands into
 * a labeled form with permission toggle cards. Disabled with a limit
 * callout when the owner has no seats left.
 */

import { useState, type FormEvent } from 'react';
import { ChevronDown, UserPlus, AlertCircle } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible';
import {
  PermissionToggleGroup,
  type DelegationPermission,
} from '@/shared/components/sub-delegation';
import type { CreateSubDelegatePayload } from '../../types/sub-delegate.type';

type InviteSubDelegateFormProps = {
  isSubmitting: boolean;
  errorMessage: string | null;
  /** When the owner has reached their effective limit, disable the form. */
  disabled?: boolean;
  onSubmit: (payload: CreateSubDelegatePayload) => Promise<void> | void;
};

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#026857] focus:outline-none focus:ring-1 focus:ring-[#026857] disabled:bg-gray-100';

export function InviteSubDelegateForm({
  isSubmitting,
  errorMessage,
  disabled = false,
  onSubmit,
}: InviteSubDelegateFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [permissions, setPermissions] = useState<DelegationPermission[]>([
    'MESSAGING',
  ]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (permissions.length === 0) return;

    await onSubmit({
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      permissions,
    });

    setEmail('');
    setFirstName('');
    setLastName('');
    setPermissions(['MESSAGING']);
  };

  const canSubmit =
    !disabled &&
    !isSubmitting &&
    email.trim().length > 0 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    permissions.length > 0;

  if (disabled) {
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        data-testid="limit-reached-callout"
      >
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          All sub-delegate seats are in use. Revoke a delegate or cancel a
          pending invitation to free up a seat.
        </span>
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-lg border border-dashed border-[#026857]/40 bg-[#026857]/5 px-4 py-3 text-sm font-medium text-[#026857] transition-colors hover:bg-[#026857]/10"
          aria-expanded={isOpen}
        >
          <span className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Invite sub-delegate
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <form
          onSubmit={handleSubmit}
          className="mt-2 space-y-4 rounded-lg border border-gray-200 bg-gray-50/60 p-4"
          aria-label="Invite a new sub-delegate"
        >
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700">
                Email
              </span>
              <input
                type="email"
                required
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                className={inputClass}
                aria-label="Email"
              />
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-700">
                  First name
                </span>
                <input
                  type="text"
                  required
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSubmitting}
                  className={inputClass}
                  aria-label="First name"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-700">
                  Last name
                </span>
                <input
                  type="text"
                  required
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSubmitting}
                  className={inputClass}
                  aria-label="Last name"
                />
              </label>
            </div>
          </div>

          <div>
            <span className="mb-1.5 block text-xs font-medium text-gray-700">
              Permissions
            </span>
            <PermissionToggleGroup
              value={permissions}
              onChange={setPermissions}
              disabled={isSubmitting}
            />
          </div>

          {errorMessage && (
            <p className="text-xs text-red-600" role="alert">
              {errorMessage}
            </p>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-[#026857] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#025246] disabled:opacity-50"
            >
              {isSubmitting ? 'Sending invitation…' : 'Send invitation'}
            </button>
          </div>
        </form>
      </CollapsibleContent>
    </Collapsible>
  );
}
